/**
 * 지원 언어와 폴백 규칙.
 *
 * 언어를 추가하려면 여기 `SUPPORTED_LANGUAGES`에 넣고
 * `data/i18n/{lang}.ts`와 `data/ui/{lang}.json`을 만든다.
 */

export const SUPPORTED_LANGUAGES = ['ko', 'en', 'ja', 'zh-cn', 'zh-tw', 'es', 'pt'] as const;
export type Language = (typeof SUPPORTED_LANGUAGES)[number];

/**
 * 언어 고르는 곳에 띄우는 이름. **그 언어로** 적는다 — 못 읽는 언어로 된
 * 화면에서 자기 언어를 찾는 사람이 읽을 수 있어야 한다.
 */
export const LANGUAGE_LABELS: Record<Language, string> = {
  ko: '한국어',
  en: 'English',
  ja: '日本語',
  'zh-cn': '简体中文',
  'zh-tw': '繁體中文',
  es: 'Español',
  pt: 'Português (Brasil)',
};

/**
 * 표준 언어 태그(BCP 47). `hreflang`·`<html lang>`·`Intl`에 쓴다.
 *
 * 주소와 저장값은 소문자 코드(`zh-cn`)를 쓰고, 바깥에 내보낼 때만 이것으로
 * 바꾼다. 포르투갈어는 브라질판이다 — 사용자가 훨씬 많고, 문구도 그쪽 말로 썼다.
 */
export const LANGUAGE_TAGS: Record<Language, string> = {
  ko: 'ko',
  en: 'en',
  ja: 'ja',
  'zh-cn': 'zh-CN',
  'zh-tw': 'zh-TW',
  es: 'es',
  pt: 'pt-BR',
};

/**
 * 중국어 지역 태그 → 간체/번체. 대만·홍콩·마카오는 번체를 쓴다.
 * `zh-Hant`/`zh-Hans`처럼 문자 체계로 적어 오는 브라우저도 있다.
 */
const TRADITIONAL_CHINESE = ['zh-tw', 'zh-hk', 'zh-mo', 'zh-hant'];

/**
 * 번역이 없을 때 돌아갈 언어. 이 언어의 번역은 **누락이 허용되지 않는다** —
 * 폴백의 폴백은 없기 때문이다(빌드가 막는다).
 */
export const FALLBACK_LANGUAGE: Language = 'en';

export function isSupportedLanguage(value: string): value is Language {
  return (SUPPORTED_LANGUAGES as readonly string[]).includes(value);
}

/**
 * `navigator.language`나 `Accept-Language` 값에서 지원 언어를 고른다.
 * "ko-KR" → "ko", "en-US" → "en", "zh-TW" → "zh-tw", "zh-Hans-CN" → "zh-cn",
 * "pt-PT" → "pt", 모르는 값 → 폴백.
 */
export function resolveLanguage(preferred: string | null | undefined): Language {
  if (preferred === null || preferred === undefined) {
    return FALLBACK_LANGUAGE;
  }
  const normalized = preferred.toLowerCase().trim();
  if (isSupportedLanguage(normalized)) {
    return normalized;
  }
  if (normalized === 'zh' || normalized.startsWith('zh-')) {
    return TRADITIONAL_CHINESE.some((tag) => normalized.startsWith(tag)) ? 'zh-tw' : 'zh-cn';
  }
  // "ko-KR" 같은 지역 태그에서 언어 부분만 떼어 본다
  const base = normalized.split('-')[0];
  if (base !== undefined && isSupportedLanguage(base)) {
    return base;
  }
  return FALLBACK_LANGUAGE;
}
